import { gql, useMutation } from "@apollo/client";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Auth } from "aws-amplify";
import { ErrorBoxDialog } from "components/error";
import { Select } from "components/select";
import copy from "copy-to-clipboard";
import { useSolutionKey } from "features/solution";
import { ScsUserType } from "model/schema/globalTypes";
import { useState } from "react";
import {
  CreateStreamingUrl,
  CreateStreamingUrlVariables
} from "./schema/CreateStreamingUrl";
export const mutationCreateStreamingUrl = gql`
  mutation CreateStreamingUrl(
    $solutionKey: Key!
    $projectId: ID!
    $refreshToken: String!
    $userType: ScsUserType!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        projects {
          createStreamingUrl(
            projectId: $projectId
            refreshToken: $refreshToken
            userType: $userType
          )
        }
      }
    }
  }
`;

const selectableUserTypes: ScsUserType[] = [
  ScsUserType.Modeler,
  ScsUserType.Standard,
  ScsUserType.Viewer
];
export const userTypeDescriptions: Partial<Record<ScsUserType, string>> = {
  [ScsUserType.Viewer]:
    "Can:\n• Access Home Screens\n• View data or Dashboards",
  [ScsUserType.Standard]:
    "Can:\n• Access Home Screens\n• View data or Dashboards\n• Edit input data\n• Execute Use Cases",
  [ScsUserType.Modeler]:
    "Can:\n• Full access (according to licensed capability packages)\n• Use Solution Explorer & Node System\n• Create new Use Cases"
};
const controlHeight = 48;

function getUserTypeStorageKey(projectId: string) {
  return `projectStreamingUserType:${projectId}`;
}

function getStoredUserType(projectId: string): ScsUserType | null {
  const storedUserType = localStorage.getItem(getUserTypeStorageKey(projectId));
  return selectableUserTypes.includes(storedUserType as ScsUserType)
    ? (storedUserType as ScsUserType)
    : null;
}

const useStyles = makeStyles(theme => ({
  secondaryButton: {
    marginRight: 5,
    height: controlHeight
  },
  startButton: {
    marginRight: 5,
    height: controlHeight,
    color: theme.palette.common.white
  },
  select: {
    display: "inline-flex",
    marginRight: 5,
    minWidth: 180,
    verticalAlign: "middle",
    "& .MuiOutlinedInput-root": {
      height: controlHeight
    },
    // MUI positions the resting label for its default 56px input.
    "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
      transform: "translate(14px, 13px) scale(1)"
    }
  }
}));

export default function ProjectStreamingButtons({
  projectId,
  isAvailable
}: {
  projectId: string;
  isAvailable: boolean;
}) {
  const solutionKey = useSolutionKey();
  const classNames = useStyles();
  const [userType, setUserType] = useState<ScsUserType | null>(() =>
    getStoredUserType(projectId)
  );
  const [isUserTypeMenuOpen, setIsUserTypeMenuOpen] = useState(false);
  const [isLicenseTooltipOpen, setIsLicenseTooltipOpen] = useState(false);

  const [
    createStreamingUrlMutation,
    { error: mutationError, reset: resetError }
  ] = useMutation<CreateStreamingUrl, CreateStreamingUrlVariables>(
    mutationCreateStreamingUrl,
    { onError: error => console.log(error) }
  );

  const createStreamingUrl = async (selectedUserType: ScsUserType) => {
    const auth = await Auth.currentSession();
    const refreshToken = auth.getRefreshToken().getToken();
    const result = await createStreamingUrlMutation({
      variables: {
        solutionKey,
        projectId,
        refreshToken,
        userType: selectedUserType
      }
    });
    return result?.data?.solutions.solution?.projects?.createStreamingUrl;
  };

  const handleStartSCS = async (selectedUserType: ScsUserType) => {
    const url = await createStreamingUrl(selectedUserType);
    if (url) window.open(url, "_blank");
  };

  return (
    <>
      <Tooltip
        title="Note: The number and type of users available to you depend on your purchased license."
        open={isLicenseTooltipOpen && !isUserTypeMenuOpen}
        onOpen={() => setIsLicenseTooltipOpen(true)}
        onClose={() => setIsLicenseTooltipOpen(false)}
        disableHoverListener={isUserTypeMenuOpen}
        disableFocusListener={isUserTypeMenuOpen}
      >
        <div className={classNames.select} data-testid="create-streaming-url">
          <Select<ScsUserType>
            options={selectableUserTypes}
            label="User type"
            id="project-streaming-user-type"
            value={userType}
            disabled={!isAvailable}
            inputStyle={{ padding: 4 }}
            getOptionTooltip={type => (
              <span style={{ whiteSpace: "pre-line" }}>
                {userTypeDescriptions[type]}
              </span>
            )}
            onOpen={() => setIsUserTypeMenuOpen(true)}
            onClose={() => {
              setIsUserTypeMenuOpen(false);
              setIsLicenseTooltipOpen(false);
            }}
            onChange={(_, selected) => {
              setUserType(selected);
              const storageKey = getUserTypeStorageKey(projectId);
              if (selected) localStorage.setItem(storageKey, selected);
              else localStorage.removeItem(storageKey);
            }}
          />
        </div>
      </Tooltip>
      <Tooltip title="Connect to this project.">
        <span>
          <Button
            variant="contained"
            className={classNames.startButton}
            disabled={!isAvailable || !userType}
            data-testid="start-streaming"
            onClick={() => {
              if (userType) handleStartSCS(userType);
            }}
            endIcon={<OpenInNewIcon />}
          >
            Start SCS
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Can be used in the AppStream Client for Windows.">
        <span>
          <Button
            variant="outlined"
            className={classNames.secondaryButton}
            disabled={!isAvailable || !userType}
            data-testid="copy-streaming-url"
            onClick={async () => {
              if (userType) {
                const url = await createStreamingUrl(userType);
                if (url) copy(url);
              }
            }}
            endIcon={<FileCopyIcon />}
          >
            Copy Streaming URL
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Can be bookmarked to connect to this project immediately.">
        <span>
          <Button
            variant="outlined"
            className={classNames.secondaryButton}
            disabled={!userType}
            data-testid="copy-connect-url"
            onClick={() => {
              if (userType) {
                const url = `${window.location.origin}${window.location.pathname}/connect/${userType}`;
                if (url) copy(url);
              }
            }}
            endIcon={<FileCopyIcon />}
          >
            Copy Connect URL
          </Button>
        </span>
      </Tooltip>

      <ErrorBoxDialog apolloError={mutationError} onClose={resetError} />
    </>
  );
}
